import { useNotification } from "@context/NotificationContext";
import type { ResolveDisputePayload } from "@services/disputes/types";
import { resolveDispute } from "@services/disputesApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

const SERVICE = "bff-vessel-call";
const LANG_BASE = "vessel-call.operations";

export const useResolveDispute = () => {
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const resolveDisputeMutation = useMutation<
    unknown,
    unknown,
    { disputeId: string; payload: ResolveDisputePayload }
  >({
    mutationFn: ({ disputeId, payload }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "Operations",
          action: "request_dispute_resolution",
          entityType: "dispute",
          entityId: disputeId,
        },
        (transactionId) => resolveDispute({ service: SERVICE, disputeId, payload, transactionId }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["disputes"] });
      await queryClient.invalidateQueries({ queryKey: ["dispute"] });
      await queryClient.invalidateQueries({ queryKey: ["vesselCall"] });
      notify({
        title: t(`${LANG_BASE}.resolve-dispute-success-title`),
        type: "success",
      });
    },
    onError: () => {
      notify({
        title: t("common.error"),
        description: t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  return {
    resolveDispute: resolveDisputeMutation.mutate,
    isResolvingDispute: resolveDisputeMutation.isPending,
  };
};
